/**
 * ImpactSignatureCard — the compact Impact Signature gateway in the My Impact
 * rail (POLICY_NAVIGATION_RAILS §7.6 as amended 2026-07-09; DEV-1446).
 *
 * SKIP-APPROVED REDESIGN (supersedes the chips/pills design): a compact
 * radar THUMBNAIL (~96px, the shared ImpactSignatureMini, presentational)
 * beside six icon-bearing HEXAGONAL MEDALS (the shared DimensionMedal — the
 * same medals the full page renders). Rules:
 *   - TWO text elements (§7.6-as-amended, ratified 2026-07-09 late): the
 *     "Impact Signature" title (chevron ok) and the sanctioned OVERALL
 *     status label ("Bronze Signature" when all six carry it, else
 *     "n of 6 dimensions lit"). Still NO per-dimension words — that variant
 *     was explicitly REJECTED (Skip's own wordless verdict holds);
 *     dimensions speak through icon-hexes + tooltips/aria only.
 *   - Earned vs unlit reads NON-chromatically: DimensionMedal renders earned
 *     as a solid-filled, solid-bordered medal and unlit as a dashed hollow
 *     hexagon — never color alone.
 *   - The sparse post-reveal state (one lit vertex, five dashed hexes) is
 *     APPROVED and reads as invitation, not failure.
 *   - Card height budget ~160px.
 *
 * Gateway contract unchanged: no counts, no actions, no urgency; click opens
 * the full signature page in the main pane (rail persistent). Pre-reveal
 * Explorers see NOTHING (locked slot removed — §7.6 superseded 2026-07-09
 * late); the card first appears at promotion, and that reveal choreography
 * (Notice → modal → card) is a binding chunk-B item, not designed here. The
 * reveal predicate is chunk B's `showImpactSurfaces` — never a second one.
 */

import React from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiImpactSignature } from "../../api/useApiImpactSignature";
import { type TierLevel } from "../../api/impactSignature";
import type { DimKey } from "../DimensionRadar/dimensionConfig";
import DimensionMedal from "../DimensionRadar/DimensionMedal";
import ImpactSignatureMini from "../DimensionRadar/ImpactSignatureMini";
import styles from "./ImpactSignatureCard.module.css";

/** Radar-canonical order (DimensionRadar vertex order). */
const DIM_ORDER: DimKey[] = ["mind", "voice", "heart", "body", "wings", "roots"];

export interface ImpactSignatureCardProps {
  /** Chunk B's reveal predicate (App.showImpactSurfaces). */
  revealed: boolean;
  /** Fetch only when the card is actually shown — pass null off-section. */
  currentUserGuid: string | null;
  /** Open the full signature page in the main pane. */
  onOpen: () => void;
  /** Collapsed-rail density (Skip redline 2026-07-09): render only the
   *  mini-radar as the icon strip's TOP ICON — same gateway, same reveal
   *  predicate, no text. */
  compact?: boolean;
  /** Skip redline 2026-07-10: the title row renders in the RAIL HEADER
   *  (in line with the collapse toggle) via ImpactSignatureHeaderTitle —
   *  suppress the in-card copy so it never appears twice. */
  hideTitle?: boolean;
  /** §7.16 beat 2 (chunk B slice D): true during the ARRIVAL window
   *  (promoted, radar not yet explained) — the card materializes with a
   *  one-shot animation instead of just being there. Reduced-motion
   *  collapses via CSS; later visits render statically. */
  arriving?: boolean;
}

/**
 * The "Impact Signature ›" title as a Rail-header element (Skip redline
 * 2026-07-10: the collapse toggle sits IN LINE with this label). Kept in
 * this file with the card so the two render sites share one i18n key and
 * one open handler shape and can never drift.
 */
export const ImpactSignatureHeaderTitle: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useAppTranslation("pages");
  return (
    <button
      type="button"
      className={styles.headerTitleBtn}
      onClick={onOpen}
      aria-label={t("impact.card.open")}
    >
      {t("impact.impactSignature")}
    </button>
  );
};

const ImpactSignatureCard: React.FC<ImpactSignatureCardProps> = ({
  revealed,
  currentUserGuid,
  onOpen,
  compact = false,
  hideTitle = false,
  arriving = false,
}) => {
  const { t } = useAppTranslation("pages");
  // Gated read — null guid skips the fetch (hook contract). Locked slot
  // never fetches (nothing truthful to show pre-reveal).
  const { tiers } = useApiImpactSignature(revealed ? currentUserGuid : null);

  // Pre-reveal: no signature element at all — the rail opens with the
  // lenses + belonging (ratified 2026-07-09 late).
  if (!revealed) return null;

  // Compact (collapsed-rail) form: the signature thumbnail alone, as the
  // strip's top icon. One component, two densities — the gateway contract
  // (click opens the full page) is identical.
  if (compact) {
    return (
      <button
        type="button"
        className={`${styles.compactBtn} ${arriving ? styles.materialize : ""}`}
        onClick={onOpen}
        aria-label={t("impact.card.open")}
        title={t("impact.impactSignature")}
      >
        <ImpactSignatureMini tiers={tiers ?? undefined} interactive={false} />
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`${styles.card} ${arriving ? styles.materialize : ""}`}
      onClick={onOpen}
      aria-label={t("impact.card.open")}
    >
      {/* Title + chevron — suppressed when the title lives in the Rail
          header row beside the collapse toggle (Skip redline 2026-07-10). */}
      {!hideTitle && (
        <span className={styles.cardTitle}>
          {t("impact.impactSignature")}
          <span className={styles.chevron} aria-hidden="true">›</span>
        </span>
      )}
      <span className={styles.cardBody}>
        <span
          className={styles.radarThumb}
          role="img"
          aria-label={t("impact.impactSignature")}
          title={t("impact.impactSignature")}
        >
          <ImpactSignatureMini tiers={tiers ?? undefined} interactive={false} />
        </span>
        <span className={styles.hexGrid}>
          {DIM_ORDER.map((d) => {
            const tier = (tiers?.[d] ?? "none") as TierLevel;
            const label =
              tier === "none"
                ? t("impact.card.hexUnlit", { dim: t(`impact.dim.${d}`) })
                : t("impact.card.hexEarned", {
                    dim: t(`impact.dim.${d}`),
                    tier: t(`impact.tier.${tier}`),
                  });
            return (
              <span key={d} className={styles.hex} title={label}>
                <DimensionMedal tier={tier} dim={d} ariaLabel={label} />
              </span>
            );
          })}
        </span>
      </span>
      {/* Status label REMOVED (Skip 2026-07-10): "Bronze Signature" needed
          explaining on a glanceable card — the artwork (filled center +
          medal tiers) carries the state; the labeled heading stays on the
          full page, where overallSignature still renders it with room for
          context. */}
    </button>
  );
};

export default ImpactSignatureCard;
